import type { GASetupDatalayerParamItems } from '@integration-layer/schemas/GAEvents/dataLayerSetup'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import type { ListResponse, Price } from '@commercelayer/sdk'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'

/**
 * Manages product page entity data for Google Analytics tracking.
 *
 * Extracts and prepares product information for data layer setup, including:
 * - Category information
 * - Stock availability
 * - Store availability
 *
 * @param {AlgoliaProductType} algoliaProduct - Product data from Algolia
 * @param {ListResponse<Price>} commercelayerProduct - Product Price data from Commercelayer
 * @returns {void}
 */
export const useGAEntityProductPage = (
  algoliaProduct: AlgoliaProductType,
  commercelayerProduct: ListResponse<Price>
) => {
  /**
   * Reactive entity for storing product page data layer setup items.
   * Excludes product_listSize and product_listtype from the original type.
   * @type {Ref<GASetupDatalayerParamItems>}
   */
  const itemsEntity =
    useGAEntity<
      Omit<GASetupDatalayerParamItems, 'product_listSize' | 'product_listtype'>
    >('GA_DL_SETUP_ITEMS')

  /**
   * Extracts hierarchical categories from the Algolia product.
   * @type {Object}
   * @property {string} category1 - First level category
   * @property {string} category2 - Second level category
   */
  const { category1, category2 } = extractCategoriesUnlocalized(
    algoliaProduct.hierarchicalCategories || [],
    algoliaProduct.categoryPageId2 || []
  )

  /**
   * Checks if the product is available in Algolia.
   * @type {boolean}
   */
  const isAlgoliaAvailable = algoliaProduct.size?.some(item => item.avEcom)

  /**
   * Checks if the product is available in Commercelayer.
   * @type {boolean}
   */
  const isClAvailable = commercelayerProduct?.some(
    el => el.sku && isInStock(el.sku)
  )

  /**
   * Determines out-of-stock status.
   * 0 indicates in stock, 1 indicates out of stock.
   * @type {number}
   */
  const itemOutOfStock = isAlgoliaAvailable && isClAvailable ? 0 : 1

  /**
   * Sets the items entity with product page tracking information.
   */
  itemsEntity.value = {
    /** First level category */
    item_category_level_1: category1,
    /** Second level category */
    item_category_level_2: category2,
    /** Out of stock indicator */
    itemOutOfStock,
    /** Shop category type */
    shop_category: 'item',
    /** Store availability indicator */
    storeAvailability: algoliaProduct.size?.some(item => item.avRet) ? 1 : 0,
  }
}
